import { Box, Divider, Grid, GridCol, Group, Stack } from '@mantine/core';
import { SourceModelInformation } from '@/app/assets/details/molecules/SourceModelInformation';
import { CollapseIcon } from '@/app/assets/details/atoms/CollapseIcon';
import { ScaleComponent } from '@/app/assets/details/molecules/scale/ScaleComponent';
import { AssetTags } from '@/app/assets/details/molecules/AssetTags';
import { useTagMutations } from '@/app/assets/details/organisms/hooks';
import { RawAssetDetailsHeader } from '@/app/assets/details/molecules/RawAssetDetailsHeader';
import { OutputAssetDetailsHeader } from '@/app/assets/details/molecules/OutputAssetDetailsHeader';
import { FileInformation } from '@/app/assets/details/molecules/FileInformation';
import { RawAssetPreview } from '@/app/components/shared/viewer/RawAssetPreview';
import { RapidModelPreview } from '@/app/components/shared/viewer/RapidModelPreview';
import { useResizeObserver } from '@mantine/hooks';
import { useSelectedAsset } from '../../hooks/useAssetsQuery';
import { AssetInfos } from '@/app/assets/components/table/molecules/AssetInfos';
import { Metadata } from '../molecules/Metadata';
import { Embeds } from '../molecules/Embeds';
import { useEffect } from 'react';
import { useOnboarding } from '../../../contexts/OnboardingContext/OnboardingContext';
import { OnboardingFlows } from '../../../contexts/OnboardingContext/constants';
import { QualityControl } from '@/app/assets/details/molecules/QualityControl';

interface AssetDetailsSidebarProps {
  onCollapse: () => void;
}

export function AssetDetailsSidebar({ onCollapse }: AssetDetailsSidebarProps) {
  const { handleTagAdd, handleTagOnDelete } = useTagMutations();
  const selectedAsset = useSelectedAsset();
  const [sizeObserverRef, sizeObserverRect] = useResizeObserver();
  const { runFlow } = useOnboarding();

  useEffect(() => {
    if (selectedAsset) {
      runFlow(OnboardingFlows.SIDEBAR);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we want to trigger this effect only when select asset changes
  }, [selectedAsset]);

  if (!selectedAsset) {
    return null;
  }

  const [wx, h, wy] = selectedAsset.rpdInfo?.bbSize ? selectedAsset.rpdInfo.bbSize : [];
  const columnWidth = 440;
  const numberOfColumns = sizeObserverRect.width < 900 ? 6 : 12;
  const gutter = numberOfColumns === 6 ? '0' : 'md';

  return (
    <Stack ref={sizeObserverRef} gap='xs'>
      <Group px='sm' mt='sm' py='xs' align='flex-start' wrap='nowrap' justify='stretch'>
        <Box flex='0 0' pt='xs'>
          <CollapseIcon onCollapse={onCollapse} />
        </Box>
        <Stack align='stretch' w='100%'>
          {selectedAsset.assetType === 'raw' && <RawAssetDetailsHeader asset={selectedAsset} />}
          {selectedAsset.assetType === 'output' && <OutputAssetDetailsHeader asset={selectedAsset} />}
        </Stack>
      </Group>
      <Divider color='gray.4' />
      <Group mx='md' align='flex-start' wrap='nowrap' justify='stretch' ref={sizeObserverRef}>
        <Stack align='stretch' w='100%' gap='xs'>
          {selectedAsset.assetType === 'raw' && (
            <AssetInfos
              errors={selectedAsset.errors}
              warnings={selectedAsset.warnings}
              assetDescription={{
                id: selectedAsset.id,
                name: selectedAsset.name,
                type: selectedAsset.assetType,
              }}
            />
          )}
          <Grid gutter={gutter} grow columns={numberOfColumns}>
            <GridCol span={6}>
              <Stack gap='0'>
                {selectedAsset.assetType === 'raw' && (
                  <RawAssetPreview sourceAsset={selectedAsset} height={sizeObserverRect.width / 2} />
                )}
                {selectedAsset?.assetType === 'output' && (
                  <RapidModelPreview
                    key={selectedAsset.id}
                    rapidModel={selectedAsset}
                    height={sizeObserverRect.width / 2}
                  />
                )}
                <FileInformation asset={selectedAsset} maw={columnWidth} />
                {selectedAsset?.assetType === 'output' && selectedAsset?.meta?.qc && (
                  <QualityControl asset={selectedAsset} />
                )}
                <AssetTags onAddTag={handleTagAdd} onDeleteTag={handleTagOnDelete} />
              </Stack>
            </GridCol>
            <GridCol span={6}>
              <Stack gap='0'>
                {selectedAsset?.rpdInfo && <ScaleComponent wx={wx} wy={wy} h={h} asset={selectedAsset} />}
                {selectedAsset?.rpdInfo && (
                  <SourceModelInformation rpdInfo={selectedAsset?.rpdInfo} maw={columnWidth} />
                )}
                <Metadata asset={selectedAsset} maw={columnWidth} />
                <Embeds asset={selectedAsset} maw={columnWidth} />
              </Stack>
            </GridCol>
          </Grid>
        </Stack>
      </Group>
    </Stack>
  );
}
